import React, { useEffect, useState, useCallback } from "react";
import { Table, Image, Tag, DatePicker, Button, notification } from "antd";
import moment from "moment";
import axios from "axios";
import Loader from "../atoms/Loader";
import styled from "styled-components";

const Head = styled.div`
  display: flex;
  min-height: 70px;
  justify-content: space-between;
  align-items: center;
  padding: 5px 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

/** API response field (Unicode key from backend) */
const API_SLOT_KEY = "boş_saatler";

function formatStartTime(t) {
  if (t == null || t === "") return "";
  const parts = String(t).split(":");
  const h = (parts[0] ?? "00").padStart(2, "0");
  const m = String(parts[1] ?? "00").padStart(2, "0");
  return `${h}:${m}`;
}

const AvailableTrainers = () => {
  const [date, setDate] = useState(() => moment());
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async (d) => {
    const dateStr = moment(d).format("DD-MM-YYYY");
    setLoading(true);
    try {
      const res = await axios.get(`trainer/available/${encodeURIComponent(dateStr)}`);
      if (res.data?.status === "success") {
        setList(res.data.list || []);
      } else {
        setList([]);
        if (res.data?.message) {
          notification.warning({ message: res.data.message });
        }
      }
    } catch (e) {
      console.error(e);
      setList([]);
      notification.error({ message: "Could not load data" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(date);
  }, [date, fetchList]);

  return (
    <>
      <Head>
        <h1 className="h3 mt-3 mb-0">
          <strong>AVAILABLE TUTORS</strong>
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span>Date:</span>
          <DatePicker
            format="DD.MM.YYYY"
            value={date}
            onChange={(d) => {
              if (d) setDate(d);
            }}
            allowClear={false}
          />
          <Button onClick={() => fetchList(date)}>Refresh</Button>
        </div>
      </Head>

      <div style={{ padding: "0 20px 24px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Loader size="md" />
          </div>
        ) : (
          <Table
            columns={[
              {
                title: "",
                dataIndex: "image",
                key: "image",
                width: 88,
                render: (_, record) => {
                  if (record.image_url && record.image) {
                    return <Image width={60} src={`${record.image_url}/${record.image}`} />;
                  }
                  return null;
                },
              },
              { title: "First name", dataIndex: "first_name", key: "first_name" },
              { title: "Last name", dataIndex: "last_name", key: "last_name" },
              { title: "Email", dataIndex: "email", key: "email" },
              { title: "Status", dataIndex: "status", key: "status" },
              {
                title: "Student type",
                dataIndex: "trainer_type",
                key: "trainer_type",
                render: (_, record) => {
                  if (record.trainer_type === 2) return <Tag color="green">Adults & kids</Tag>;
                  if (record.trainer_type === 1) return <Tag color="blue">Adults only</Tag>;
                  return <Tag>-</Tag>;
                },
              },
              {
                title: "Child ages",
                key: "child_ages",
                width: 120,
                render: (_, record) => {
                  if (record.trainer_type !== 2) return <span style={{ color: "#999" }}>—</span>;
                  const parts = [];
                  if (record.trainer_child_age_4_8) parts.push("4–8");
                  if (record.trainer_child_age_9_12) parts.push("9–12");
                  if (record.trainer_child_age_13_17) parts.push("13–17");
                  if (!parts.length) return <span style={{ color: "#999" }}>—</span>;
                  return (
                    <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {parts.map((p) => (
                        <Tag key={p}>{p}</Tag>
                      ))}
                    </span>
                  );
                },
              },
              {
                title: "Available slots (start)",
                key: "slots",
                render: (_, record) => {
                  const slots = record[API_SLOT_KEY] || [];
                  const parts = slots.map((s) => formatStartTime(s.start_time)).filter(Boolean);
                  if (!parts.length) {
                    return <span style={{ color: "#999" }}>—</span>;
                  }
                  return (
                    <span style={{ lineHeight: 1.7, whiteSpace: "normal", wordBreak: "break-word" }}>
                      {parts.join(" - ")}
                    </span>
                  );
                },
              },
            ]}
            dataSource={list}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: "No available tutors for this date" }}
          />
        )}
      </div>
    </>
  );
};

export default AvailableTrainers;
